import React from 'react';
import { useTheme } from '@mui/material';
import { MarkerF, OverlayView, Polyline, useGoogleMap } from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  addRulerPoint,
  insertRulerPoint,
  removeRulerPoint,
  selectRulerActive,
  selectRulerPoints,
  setRulerActive,
  updateRulerPoint,
} from '@src/core/controllers/map/redux';
import DistanceLabel from '@src/core/controllers/map/components/distance-label';

const RULER_NO_ADD_ATTR = 'data-ruler-no-add';
const DELETE_BADGE_SIZE = 24;
const DELETE_ICON_SIZE = 16;
const LINE_WEIGHT = 3;
const LINE_BORDER = 1;

const deleteBadgeOffset = (): { x: number; y: number } => ({ x: 8, y: -(DELETE_BADGE_SIZE + 6) });

const RulerLayer: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const active = useSelector(selectRulerActive);
  const points = useSelector(selectRulerPoints);
  const map = useGoogleMap();

  const rulerColor = theme.palette.brand.main;
  const white = theme.palette.common.white;
  const black = theme.palette.common.black;

  const [hovered, setHovered] = React.useState<number | null>(null);

  React.useEffect(() => {
    return () => {
      dispatch(setRulerActive(false));
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!active) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      dispatch(setRulerActive(false));
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, dispatch]);

  React.useEffect(() => {
    if (!map || !active) return;

    map.setOptions({ draggableCursor: 'crosshair' });
    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      // Clicks on the delete badge can reach the map's click listener and would
      // otherwise drop a brand-new point right where the user just deleted one.
      // Skip when the originating DOM node belongs to a ruler control.
      const target = (e.domEvent as MouseEvent | undefined)?.target as HTMLElement | null;
      if (target?.closest(`[${RULER_NO_ADD_ATTR}]`)) return;
      if (!e.latLng) return;
      dispatch(addRulerPoint(e.latLng.toJSON()));
    });

    return () => {
      google.maps.event.removeListener(listener);
      // null resets to the map's default grab cursor.
      map.setOptions({ draggableCursor: null });
    };
  }, [map, active, dispatch]);

  const midpoints = React.useMemo(() => {
    if (!active || points.length < 2) return [];
    if (!google.maps.geometry?.spherical) return [];
    const result: { position: google.maps.LatLngLiteral; insertAt: number }[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = new google.maps.LatLng(points[i].lat, points[i].lng);
      const b = new google.maps.LatLng(points[i + 1].lat, points[i + 1].lng);
      result.push({
        position: google.maps.geometry.spherical.interpolate(a, b, 0.5).toJSON(),
        insertAt: i + 1,
      });
    }
    return result;
  }, [active, points]);

  if (!active) return null;

  const mainIcon: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: white,
    fillOpacity: 1,
    strokeColor: rulerColor,
    strokeWeight: 3,
  };

  const midIcon: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 5,
    fillColor: white,
    fillOpacity: 0.75,
    strokeColor: rulerColor,
    strokeWeight: 1,
  };

  return (
    <React.Fragment>
      {points.length >= 2 && (
        <React.Fragment>
          {/* White outline drawn underneath gives the line a 1px white border on each side. */}
          <Polyline
            path={points}
            options={{
              clickable: false,
              strokeColor: white,
              strokeOpacity: 1,
              strokeWeight: LINE_WEIGHT + LINE_BORDER * 2,
              zIndex: 40,
            }}
          />
          <Polyline
            path={points}
            options={{
              clickable: false,
              strokeColor: rulerColor,
              strokeOpacity: 1,
              strokeWeight: LINE_WEIGHT,
              zIndex: 41,
            }}
          />
        </React.Fragment>
      )}

      <DistanceLabel path={points} />

      {midpoints.map((m) => (
        <MarkerF
          key={`ruler-mid-${m.insertAt}`}
          position={m.position}
          icon={midIcon}
          draggable
          zIndex={50}
          onDragEnd={(e) => {
            if (!e.latLng) return;
            dispatch(insertRulerPoint({ index: m.insertAt, point: e.latLng.toJSON() }));
          }}
        />
      ))}

      {points.map((point, index) => (
        <React.Fragment key={`ruler-point-${index}`}>
          <MarkerF
            position={point}
            icon={mainIcon}
            draggable
            zIndex={60}
            onMouseOver={() => setHovered(index)}
            onMouseOut={() => setHovered((cur) => (cur === index ? null : cur))}
            onDragEnd={(e) => {
              if (!e.latLng) return;
              dispatch(updateRulerPoint({ index, point: e.latLng.toJSON() }));
            }}
          />
          {hovered === index && (
            <OverlayView
              position={point}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={deleteBadgeOffset}>
              <div
                role='button'
                aria-label='Delete point'
                {...{ [RULER_NO_ADD_ATTR]: true }}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered((cur) => (cur === index ? null : cur))}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(removeRulerPoint(index));
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: DELETE_BADGE_SIZE,
                  height: DELETE_BADGE_SIZE,
                  borderRadius: '50%',
                  background: white,
                  color: black,
                  border: '1px solid rgba(0,0,0,0.2)',
                  fontSize: DELETE_ICON_SIZE,
                  lineHeight: 1,
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  userSelect: 'none',
                }}>
                ×
              </div>
            </OverlayView>
          )}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};

export default RulerLayer;
